import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@ptl/angular-redux-store';
import { savePlanIdForEnrollment, savePlanNameForEnrollment } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { MatDialog } from '@angular/material/dialog';
import { VendorRedirectModalComponent } from 'src/app/angular-elements/plan-selection/plan-card/vendor-redirect-modal/vendor-redirect-modal.component';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';

import { Observable, Subscription, pipe } from 'rxjs';
import { take } from 'rxjs/operators';
import { EffectiveDateStateStructure } from '../configs/interfaces/effective-date.state-structure.interface';
import { ContentStorageService, PtlWindowLocationService } from '@ptl/global-components';
import { DateService } from 'src/app/shared/services/date.service';
import { PRODUCT_GROUPS } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonUtilityService } from '../../../../shared/utility-services/common-utility.service';
import { OnExchangeEnrollmentCheckService } from '../../services/enrollment/on-exchange/on-exchange-enrollment-service';
import { messageCatalogModalContentKey } from '../message-catalog-modal-trigger/enums/message-catalog-modal-trigger.enum';
import { MessageCatalogModalTriggerService } from '../message-catalog-modal-trigger/services/message-catalog-modal-trigger.service';

@Component({
    selector: 'app-enroll-button',
    templateUrl: './enroll-button.component.html',
    styleUrls: ['./enroll-button.component.scss'],
    standalone: false
})
export class EnrollButtonComponent implements OnInit, OnDestroy {

  @Input() enrollStartUrl: string;
  @Input() allowEnroll: boolean = true;
  @Input() plan: Product<PlanAttributes, SummaryView>;
  @Input() specialEnrollmentUrl: string;
  @Input() isSep: boolean | string;

  isWindowShopping: boolean = false;
  useDownloadLink: boolean = false;
  useDownloadLinkLabel: string;
  sellNextYearProductsDate: boolean = false;
  effectiveDate: EffectiveDateStateStructure;
  yearSelected = this.date.getTodayAsMoment().get('year').toString();
  productName = 'unknown_product';
  isSepAsBool: boolean;

  @select(['appStates', 'ShoppingDates']) shoppingDates$: Observable<any>;
  shoppingDatesSubscription: Subscription;
  @select(['appStates', 'EffectiveDate']) effectiveDate$: Observable<any>;
  effectiveDateSubscription: Subscription;

  processingONX: boolean;

  enrollButtonText = 'ENROLL';

  messageCatalogModalSubscription: Subscription;
  messageCatalogDisplaySubscription: Subscription;
  messageCatalogContent: Map<string, any>;
  displayMessagesFound: boolean;

  constructor(
    private ngRedux: NgRedux<any>,
    private matDialog: MatDialog,
    private windowLocationService: PtlWindowLocationService,
    private contentStorageService: ContentStorageService,
    private date: DateService,
    private commonUtilityService: CommonUtilityService,
    private onExchangeCheckService: OnExchangeEnrollmentCheckService,
    private snackBar: MatSnackBar,
    private messageCatalogModalService: MessageCatalogModalTriggerService
  ) { }

  ngOnDestroy() {
    if (this.shoppingDatesSubscription) { this.shoppingDatesSubscription.unsubscribe(); }
    if (this.effectiveDateSubscription) { this.effectiveDateSubscription.unsubscribe(); }
    if (this.messageCatalogModalSubscription) { this.messageCatalogModalSubscription.unsubscribe(); }
    if (this.messageCatalogDisplaySubscription) { this.messageCatalogDisplaySubscription.unsubscribe(); }
  }
  ngOnInit() {
    this.messageCatalogModalSubscription = this.contentStorageService.contentChanged$.subscribe(async () => {
      this.messageCatalogContent = this.contentStorageService.getContentMap(messageCatalogModalContentKey);
    });

    
    if (this.isSep?.toString() === "true") {
      this.isSepAsBool = true;
    }

    this.productName = this.plan.attributes.PRODUCT_NAME.replace(/ /g, '-');

    this.allowEnroll = this.plan.allowEnroll;

    if (this.allowEnroll === true) {
      this.checkDateSensitiveProductsAllowEnrollment()
    }

    const content: Map<any, any> = this.contentStorageService.getContentMap('useDownloadLink');

    if (content?.size > 0) {
      this.configureContentOverrides(content);
    }

  }

  get isDownloadDocument() {
    return this.useDownloadLink || false;
  }
  
  checkDateSensitiveProductsAllowEnrollment() {

    // TODO: Get these products from a Configuration.
    const dateSensitiveProducts = [
      PRODUCT_GROUPS.MEDICARE_ADVANTAGE.toUpperCase(),
      PRODUCT_GROUPS.PRESCRIPTION_DRUG_PLAN.toUpperCase(),
      PRODUCT_GROUPS.WHOLECARE.toUpperCase(),
      PRODUCT_GROUPS.DSNP.toUpperCase(),
      PRODUCT_GROUPS.OFF_EXCHANGE.toUpperCase(),
      // PRODUCT_GROUPS.DENTAL.toUpperCase(), // Dental is year-round, so may not be date-sensitive
      PRODUCT_GROUPS.ON_EXCHANGE.toUpperCase()
    ];

    if (this.allowEnroll === true && dateSensitiveProducts.includes(this.plan.attributes.PRODUCT_GROUP)) {

      this.shoppingDatesSubscription = this.shoppingDates$.subscribe((value) => {
        this.sellNextYearProductsDate = this.date.getTodayAsMoment().isSameOrAfter(value.sellNextYearProductsDate);
        this.isWindowShopping = (
          this.date.getTodayAsMoment().isSameOrAfter(value.showNextYearProductsDate) &&
          this.date.getTodayAsMoment().isBefore(value.sellNextYearProductsDate)
        );
      });

      this.effectiveDateSubscription = this.effectiveDate$.subscribe((value) => {
        this.effectiveDate = value || {};
        this.yearSelected = this.effectiveDate?.effectiveDateYear;
        const isYearSameAsYearSelected = this.date.getTodayAsMoment().year() === Number(this.yearSelected);
        if (!isYearSameAsYearSelected && !this.isSepAsBool) {
          this.allowEnroll = this.sellNextYearProductsDate;
        }
      });
    }
  }

  goToEnrollFlow() {


    // TODO: DC - Rewrite this logic to not be so explicit.
    if (this.plan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.ON_EXCHANGE.toUpperCase()) {
      this.onExchangeCheckService.doOnXEnrollChecks(this.enrollStartUrl, this.plan);

    } else if (this.isDownloadDocument) {
        if (this.messageCatalogContent.size > 0) {
          const showModalOnEnroll = this.messageCatalogContent.get('showModalOnEnroll');
          if(showModalOnEnroll){
            if(!this.messageCatalogContent.get("enrollRightButtonRedirectUrl")){
              this.messageCatalogContent.set("enrollRightButtonRedirectUrl", this.specialEnrollmentUrl);
            }
            const checkPlanAndZip = this.messageCatalogModalService.checkPlanAndZipMessages(this.messageCatalogContent, true, this.plan);
            this.messageCatalogDisplaySubscription = this.messageCatalogModalService.displayResponseSubject.pipe(take(1)).subscribe(response => {
              if (response && response[0].size > 0) {
                this.messageCatalogModalService.openMessageCatalogModal(this.messageCatalogContent, response[0], true);
              }else {
                console.error("No Messages Found to Display");
                this.windowLocationService.goToUrl(this.specialEnrollmentUrl, { newTab: true });
              }
            });   
        }
      }else{
        this.windowLocationService.goToUrl(this.specialEnrollmentUrl, { newTab: true });
      }
    } else if (!this.isProducerDsnpFlow() && this.isVendorPlan()) {

      this.openVendorUrlRedirectModal();
    } else {
      
      this.ngRedux.dispatch(savePlanIdForEnrollment(this.plan.attributes.PLAN_ID));
      this.ngRedux.dispatch(savePlanNameForEnrollment(this.plan.productName));

      const alertDuration = 1000;

      this.snackBar.open(`Saving Selected Plan.`, 'OK', {
        duration: alertDuration,
        panelClass: 'positive-panel-class'
      });

      setTimeout(() => {
        const productName = this.plan.attributes.PRODUCT_NAME;
        const finalUrl = `${this.enrollStartUrl}?planName=${productName}`;
        if (this.messageCatalogContent.size > 0) {      
          const showModalOnEnroll = this.messageCatalogContent.get('showModalOnEnroll');  
          if (showModalOnEnroll){
            if(!this.messageCatalogContent.get("enrollRightButtonRedirectUrl")){
              this.messageCatalogContent.set("enrollRightButtonRedirectUrl", finalUrl);
            }
            const checkPlanAndZip = this.messageCatalogModalService.checkPlanAndZipMessages(this.messageCatalogContent, true, this.plan);
            this.messageCatalogDisplaySubscription = this.messageCatalogModalService.displayResponseSubject.pipe(take(1)).subscribe(response => {
              if (response && response[0].size > 0 && showModalOnEnroll) {
                this.messageCatalogModalService.openMessageCatalogModal(this.messageCatalogContent, response[0], true);
              }else {
                console.error("No Messages Found to Display");
                this.windowLocationService.goToUrl(finalUrl); 
              }
            });    
          }
        }else{
          this.windowLocationService.goToUrl(finalUrl);
        }
      }, alertDuration)



    }
  }

  

  openVendorUrlRedirectModal() {
    const urlToVendorSite: string = this.plan.attributes.VENDOR_URL;

    // Don't show leaving site modal if this is flagged as a trusted URL
    if (this.plan.attributes.VENDOR_TRUSTED?.toUpperCase() === 'TRUE') {
      const isSameTab = this.plan.attributes.VENDOR_SAME_TAB?.toUpperCase() === 'TRUE';
      this.windowLocationService.goToUrl(urlToVendorSite, { newTab: !isSameTab });
      return;
    }

    this.matDialog.open(VendorRedirectModalComponent, {
      data: {
        vendorUrl: urlToVendorSite
      }
    })
  }

  isVendorPlan(): boolean {
    if (this.plan?.attributes?.VENDOR_ENROLL?.toUpperCase() === 'TRUE') {
      return true;
    }

    return false;
  }

  isProducerDsnpFlow(): boolean {
    return !this.commonUtilityService.isConsumer() && this.plan.attributes.PRODUCT_GROUP === PRODUCT_GROUPS.DSNP;
  }

  configureContentOverrides(content: Map<any, any>) {

    const ignoreIfShoppingPeriod = !(content.get('ignoreDuringShoppingPeriod') === 'true' && this.sellNextYearProductsDate);

    if (ignoreIfShoppingPeriod && content.get('useDownloadLink') && content.get('useDownloadLink') === 'true') {
      this.useDownloadLinkLabel = content.get('useDownloadLinkLabel');
      // TODO: this.specialEnrollmentUrl >> change all of these to not say SpecialEnrollment, or "download" and instead "openNewTab".
      // And allow override from content storage service. Will also require CONTENT/CONFIG changes in AEM to align when released.
      this.enrollButtonText = this.useDownloadLinkLabel || 'SELECT PLAN'
      this.useDownloadLink = true;
    } else {
      this.enrollButtonText = this.useDownloadLinkLabel || 'ENROLL'
      this.useDownloadLink = false;
    }

    // TODO: Re-enable this after more testing is done.
    // if (content.get('forceAllowEnroll') === 'true') {
    //   // NOTE: This is done this way to not override allowEnroll if the field isn't present in contentStorage.
    //   this.allowEnroll = true;
    // }
  }
}
